/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Text, TextField, ErrorText, Pressable } from '@components/atoms'

import { InputAdornment, Select, Typography, MenuItem } from '@mui/material'
import {
	ParsedCountry,
	defaultCountries,
	FlagImage,
	parseCountry,
	usePhoneInput,
} from 'react-international-phone'

import {
	useAppDispatch,
	useAppSelector,
	paymentActions,
	PaymentStateType,
	PaymentCustomerInfoType,
} from '@redux/index'
import { CustomerType } from '@schemas'
import { produce } from 'immer'
import { AsYouType, CountryCode } from 'libphonenumber-js'

export const PaymentCustomerInfo = () => {
	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// redux state
	const isLogedin: boolean = useAppSelector(state => state.customerAuth.isLogedin)
	const reduxCustomer: CustomerType = useAppSelector(state => state.customerAuth.customer)
	const paymentMethod: string = useAppSelector(state => state.payment.paymentMethod)
	const paymentCustomer: PaymentCustomerInfoType = useAppSelector(
		state => state.payment.paymentCustomerInfo,
	)
	const errorMessages: PaymentStateType['paymentErrorMessages'] = useAppSelector(
		state => state.payment.paymentErrorMessages,
	)

	// state
	const [openPhoneCountryCode, setOpenPhoneCountryCode] = React.useState<boolean>(false)

	React.useEffect(() => {
		if (isLogedin && reduxCustomer.id !== '' && paymentCustomer.email === '') {
			const newCustomer = produce(paymentCustomer, draft => {
				draft.email = reduxCustomer.email
			})

			reduxDispatch(
				paymentActions.PAYMENT_CUSTOMER_INFO({
					...newCustomer,
				}),
			)
		}
	}, [isLogedin, reduxCustomer, paymentCustomer])

	const onGivenNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const str = e.target.value

		const newCustomer = produce(paymentCustomer, draft => {
			draft.givenName = str
		})

		reduxDispatch(
			paymentActions.PAYMENT_CUSTOMER_INFO({
				...newCustomer,
			}),
		)
	}

	const onFamilyNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const str = e.target.value

		const newCustomer = produce(paymentCustomer, draft => {
			draft.familyName = str
		})

		reduxDispatch(
			paymentActions.PAYMENT_CUSTOMER_INFO({
				...newCustomer,
			}),
		)
	}

	const onEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const str = e.target.value

		const newCustomer = produce(paymentCustomer, draft => {
			draft.email = str
		})

		reduxDispatch(
			paymentActions.PAYMENT_CUSTOMER_INFO({
				...newCustomer,
			}),
		)
	}

	const { inputValue, handlePhoneValueChange, inputRef, country, setCountry } =
		usePhoneInput({
			defaultCountry: 'us',
			value: paymentCustomer.phoneCountryCode,
			countries: defaultCountries,
			onChange: data => onPhoneCountryCodeChange(data),
		})

	const onPhoneCountryCodeChange = (data: {
		phone: string
		inputValue: string
		country: ParsedCountry
	}) => {
		const asYouType = new AsYouType(data.country.iso2.toUpperCase() as CountryCode)
		asYouType.input(data.phone)

		const coutryCode: string = data.country.dialCode

		const newCustomer = produce(paymentCustomer, draft => {
			draft.phoneCountryCode = coutryCode
		})

		reduxDispatch(
			paymentActions.PAYMENT_CUSTOMER_INFO({
				...newCustomer,
			}),
		)
	}

	const onPhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const str = e.target.value

		const newCustomer = produce(paymentCustomer, draft => {
			draft.phoneNumber = str
		})

		reduxDispatch(
			paymentActions.PAYMENT_CUSTOMER_INFO({
				...newCustomer,
			}),
		)
	}

	return (
		<View css={style} className="infoView">
			<View className="infoTitleView">
				<Text category="h3">Customer Info</Text>
			</View>

			<View className="infoInnerView customer">
				<View className="infoSubtitleView">
					<Text category="p1" className="infoSubtitle">
						The contact information below will be used for shipping.
					</Text>
					<Text category="p1" className="infoSubtitle">
						If the information is incorrect, you will not be able to receive communication
						about delivery.
					</Text>
				</View>

				{paymentMethod === 'credit' && (
					<View className="inputOuterView">
						<View className="inputView left">
							<TextField
								type="text"
								label="Given Name *"
								error={errorMessages.customer.givenName !== ''}
								onChange={onGivenNameChange}
								value={paymentCustomer.givenName}
							/>
							{errorMessages.customer.givenName !== '' && (
								<ErrorText className="errorMsg">
									{errorMessages.customer.givenName}
								</ErrorText>
							)}
						</View>

						<View className="inputView right">
							<TextField
								type="text"
								label="Family Name *"
								error={errorMessages.customer.familyName !== ''}
								onChange={onFamilyNameChange}
								value={paymentCustomer.familyName}
							/>
							{errorMessages.customer.familyName !== '' && (
								<ErrorText className="errorMsg">
									{errorMessages.customer.familyName}
								</ErrorText>
							)}
						</View>
					</View>
				)}

				{paymentMethod === 'credit' && (
					<View className="inputView">
						<TextField
							type="text"
							label="Email *"
							onChange={onEmailChange}
							value={paymentCustomer.email}
							error={errorMessages.customer.email !== ''}
						/>
						{errorMessages.customer.email !== '' && (
							<ErrorText className="errorMsg">{errorMessages.customer.email}</ErrorText>
						)}
					</View>
				)}

				<View className="inputOuterView">
					<View className="inputView phonCountryCode">
						<Pressable
							onClick={() => {
								setOpenPhoneCountryCode(!openPhoneCountryCode)
							}}
							className="phonCountryCodeButton"
						>
							<TextField
								label="Phone Country Code *"
								value={inputValue}
								onChange={handlePhoneValueChange}
								type="tel"
								error={errorMessages.customer.phoneCountryCode !== ''}
								inputRef={inputRef}
								inputProps={{ readOnly: true }}
								InputProps={{
									startAdornment: (
										<InputAdornment
											position="start"
											style={{ marginRight: '2px', marginLeft: '-8px' }}
										>
											<Select
												open={openPhoneCountryCode}
												onClose={() => setOpenPhoneCountryCode(false)}
												onOpen={() => setOpenPhoneCountryCode(true)}
												MenuProps={{
													style: {
														height: '300px',
														width: '360px',
														top: '10px',
														left: '-34px',
													},
													transformOrigin: {
														vertical: 'top',
														horizontal: 'left',
													},
												}}
												sx={{
													width: 'max-content',
													// Remove default outline (display only on focus)
													fieldset: {
														display: 'none',
													},
													'&.Mui-focused:has(div[aria-expanded="false"])': {
														fieldset: {
															display: 'block',
														},
													},
													// Update default spacing
													'.MuiSelect-select': {
														padding: '8px',
														paddingRight: '24px !important',
													},
													svg: {
														right: 0,
													},
												}}
												value={country.iso2}
												onChange={e => setCountry(e.target.value)}
												renderValue={value => {
													return <FlagImage iso2={value} style={{ display: 'flex' }} />
												}}
											>
												{defaultCountries.map(c => {
													const country = parseCountry(c)
													return (
														<MenuItem key={country.iso2} value={country.iso2}>
															<FlagImage
																iso2={country.iso2}
																style={{ marginRight: '8px' }}
															/>
															<Typography marginRight="8px">{country.name}</Typography>
															<Typography color="gray">+{country.dialCode}</Typography>
														</MenuItem>
													)
												})}
											</Select>
										</InputAdornment>
									),
								}}
							/>
						</Pressable>

						{errorMessages.customer.phoneCountryCode !== '' ? (
							<ErrorText className="errorMsg">
								{errorMessages.customer.phoneCountryCode}
							</ErrorText>
						) : null}
					</View>
					<View className="inputView phoneNumber">
						<TextField
							label="Phone Number *"
							value={paymentCustomer.phoneNumber}
							onChange={onPhoneChange}
							type="tel"
							error={errorMessages.customer.phoneNumber !== ''}
						/>

						{errorMessages.customer.phoneNumber !== '' && (
							<ErrorText className="errorMsg">
								{errorMessages.customer.phoneNumber}
							</ErrorText>
						)}
					</View>
				</View>
			</View>
		</View>
	)
}

const style = css({})
